import { BigWin } from "@westpierstudio/core-module-oryx/components/bigwin";
import { SlotGameUIView } from "@westpierstudio/core-module-oryx/components/ui/slot-game-ui/SlotGameUIView";
import { Flow } from "@westpierstudio/luma/utils/flow";
import { Reels } from "components/reels/Reels";
import { SpinType } from "components/reels/ReelsModel";
import { WinLines } from "components/winlines/WinLines";
import { GameState, PrinceOfThievesGameModel } from "game/PrinceOfThievesGameModel";
import { PrinceOfThievesGameClient } from "game/PrinceOfThievesGameClient";
import { InitialSpinEvent } from "servertypes/gameevents/InitialSpin";
import { BonusSymbolID, LastEvent, ReelSetSymbols, SymbolPos } from "servertypes/SharedTypes";
import { Signal } from "type-signals";
import { PrinceOfThievesGameView } from "game/PrinceOfThievesGameView";
import { getSoundPlayer } from "sound/SoundLocator";
import { MinTimeBetweenWagersAction } from "game/actions/MinTimeBetweenWagersAction";
import { GameEvent } from "servertypes/ServerEvents";
import { Tween } from "@westpierstudio/luma/createjs";
import { GameLogo } from "components/GameLogo";
import { Background } from "components/Background";
import { Transition } from "components/Transition";
import { Jackpot } from "components/jackpot/Jackpot";
import { mod } from "utils/Misc";
import { CrossbowBonus } from "components/CrossbowBonus";
import { FreeSpinCounter } from "components/FreeSpinCounter";
import { GameServices } from "game/GameServices";
import { TouchStartFreeSpins } from "components/TouchStartFreeSpins";

type Components = {
    reels: Reels,
    bigWin: BigWin,
    winlines: WinLines,
    gameLogo: GameLogo,
    background: Background,
    transition: Transition,
    jackpot: Jackpot
    crossbowBonus: CrossbowBonus
    freeSpinCounter: FreeSpinCounter
    touchStartFreeSpins: TouchStartFreeSpins
}

export class InitialSpinEventHandler {
    signals = {
        reelSpin: new Signal<(spinResult: ReelSetSymbols, spinType: SpinType) => void>(),
        startBigWin: new Signal<(event: InitialSpinEvent) => void>(),
        startFreeSpinPrompt: new Signal(),
        startLeafTransition: new Signal(),
        showWinlines: new Signal<(event: InitialSpinEvent, spinType: SpinType) => void>(),
        stopWinAnims: new Signal(),
        clearSkip: new Signal(),
        sessionComplete: new Signal<(event: InitialSpinEvent) => void>(),
    };

    constructor(
        private readonly services: GameServices,
        private readonly model: PrinceOfThievesGameModel,
        private readonly view: PrinceOfThievesGameView,
        private readonly client: PrinceOfThievesGameClient,
        private readonly components: Components) {
    }

    run(flow: Flow, event: InitialSpinEvent, gameUI: SlotGameUIView, minTimeBetweenWagers: number): void {

        const { betData, nextActions, totalMoneyWon } = event.body.data;
        const { reels: spinResult, freeSpinsCounter, paylineWins } = event.body.data.gameplay;
        const { resources } = this.services;
        const { reels, bigWin, winlines, gameLogo, background, transition, jackpot, crossbowBonus, freeSpinCounter, touchStartFreeSpins } = this.components;

        const spinType = this.model.getSpinType(false);
        flow.reset();
        flow.addCallback(() => {
            reels.applyGamePadding(GameEvent.INITIAL_SPIN);
            background.setBackgroundBaseGame();
            this.signals.reelSpin.dispatch(spinResult, spinType);
        })
            .addSignalBlock(reels.signals.spinComplete)
            .addCallback(() => {

                if (this.model.reels.activeSpinConfig.logSpinTime) {
                    console.log(`Spin Time(ms): `, Date.now() - this.model.reels.startTime);
                }

                this.signals.clearSkip.dispatch();
            });

        if (this.model.bigWin) {
            if (this.model.jackpot) {
                flow.addCallback(() => {
                    jackpot.startJackpot(paylineWins);
                    getSoundPlayer().bonusBell();
                }).addDelay(3000);
            }
            flow.addCallback(() => {
                this.model.state = GameState.SHOW_BIG_WIN;
            })
                .addCallback(() => this.signals.startBigWin.dispatch(event))
                .addSignalBlock(bigWin.signals.complete);
        }

        if (this.model.winlines.lineCount > 0) {
            flow.addCallback(() => this.signals.showWinlines.dispatch(event, spinType));

            if (!this.model.bigWin) {
                flow.addCallback(() => getSoundPlayer().startRollup(totalMoneyWon, betData.playerBet))
                    .addSignalBlock(gameUI.signals.winRollupComplete)
                    .addCallback(() => this.signals.clearSkip.dispatch());
            }
            if (this.model.jackpot) {
                flow.addCallback(() => jackpot.stopJackpot());
            }
        }

        if (nextActions) {

            //Server not currently providing bonus symbol data as part of the reponse before entering the bonus
            const bonusSymbols = this.getBonusSymbols(spinResult);

            flow.addCallback(() => {
                gameUI?.stopAutoPlay();
                this.signals.stopWinAnims.dispatch();

                winlines.showBonusWin(bonusSymbols);
                reels.startScatterWinAnimationSequences(bonusSymbols);
                gameLogo.bonusInitiatedMessage();
                reels.bonusInitiatedAnimations(bonusSymbols);
                getSoundPlayer().bonusBell();
                background.prepareFreeGameTransition();
                Tween.get(this)
                    .wait(resources.getSetting(`transitionDelay`))
                    .call(() => {
                        //Transition starts 1 second after bell starts (see content key)
                        getSoundPlayer().startBonusMusic();
                        transition.doLeafTransition(() => {
                            gameLogo.setSpinType(GameEvent.FREE_SPIN);
                            reels.applyGamePadding(GameEvent.FREE_SPIN);
                            background.setBackgroundFreeGame();
                            crossbowBonus.visible = true;
                            reels.setCurrentReelSymbolsToBonus();
                            winlines.reset();
                            freeSpinCounter.visible = true;
                            if (freeSpinsCounter && freeSpinsCounter.additionalFreeSpins) {
                                getSoundPlayer().youveWon(freeSpinsCounter.additionalFreeSpins!);
                                freeSpinCounter.setFreeSpins(freeSpinsCounter.freeSpinsRemaining, freeSpinsCounter.freeSpinsTotal);
                            }
                        });
                        this.signals.startLeafTransition.dispatch();
                    });
            })
                .addSignalBlock(transition.signals.leafComplete)
                .addCallback(() => {
                    winlines.reset();
                    touchStartFreeSpins.show();
                    this.signals.startFreeSpinPrompt.dispatch();
                })
                .addSignalBlock(touchStartFreeSpins.signals.complete)
                .addCallback(() => {
                    this.client.sendFreeSpinRequest(betData, this.model.freeRoundId);
                });
        } else {

            flow.add(new MinTimeBetweenWagersAction(minTimeBetweenWagers))
                .addCallback(() => this.signals.sessionComplete.dispatch(event));
        }

        flow.run();
    }

    resume(flow: Flow, lastEvent: LastEvent): void {

        const { betData } = lastEvent.data;
        const { freeSpinsCounter } = lastEvent.data.gameplay;

        const { reels, touchStartFreeSpins, freeSpinCounter } = this.components;

        flow.addCallback(() => {
            freeSpinCounter.visible = true;
            freeSpinCounter.setFreeSpins(freeSpinsCounter!.freeSpinsRemaining, freeSpinsCounter!.freeSpinsTotal);
            touchStartFreeSpins.show();
            this.signals.startFreeSpinPrompt.dispatch();
        })
            .addSignalBlock(touchStartFreeSpins.signals.complete)
            .addCallback(() => {
                this.client.sendFreeSpinRequest(betData, this.model.freeRoundId);
            });
    }

    getBonusSymbols(spinResult: ReelSetSymbols): SymbolPos[] {

        const out: SymbolPos[] = [];

        for (const [i, col] of spinResult.entries()) {
            for (const [j, symbol] of col.entries()) {

                if (symbol === BonusSymbolID.B1) {
                    out.push({ row: j, column: i });
                }
            }
        }

        return out;
    }
}